import React, { useState } from 'react';
import {
  Box,
  Button,
  CardContent,
  Divider,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { ProductCustomSpecifications } from 'src/types';
import DeleteIcon from '@material-ui/icons/Delete';

import { colors } from 'src/constants';
import { cloneDeep } from 'lodash';
import { useSnackBar } from 'src/hooks';

interface Props {
  customSpecs?: ProductCustomSpecifications;
  onChangeCustomSpecs?: (newSpecs: ProductCustomSpecifications) => void;
  hasDivider?: boolean;
}

const useStyles = makeStyles((theme) => ({
  fieldsContainer: {
    flexDirection: 'row',
    display: 'flex'
  },
  subHeader: {
    marginBottom: theme.spacing(2)
  },
  valueInput: {
    marginLeft: theme.spacing(1)
  },
  addBtn: {
    marginLeft: 'auto',
    marginTop: theme.spacing(2)
    // height: 50,
    // width: 200,
    // display: 'block'
  },
  customSpecsContainer: {
    marginTop: theme.spacing(3)
  },
  customSpecsValue: {
    marginBottom: theme.spacing(1),
    '&:hover': {
      opacity: 0.7
    }
  },
  btnIcon: {
    padding: 0,
    zIndex: 99,
    marginRight: theme.spacing(1),
    backgroundColor: colors.common.white
  }
}));

const component = ({
  customSpecs = [],
  onChangeCustomSpecs,
  hasDivider = true
}: Props) => {
  const classes = useStyles();
  const snackBar = useSnackBar();

  const [valueInput, setValueInput] = useState('');

  const onChangeCustomSpecsInternal = (
    newSpecsArr: ProductCustomSpecifications
  ) => {
    if (onChangeCustomSpecs) {
      onChangeCustomSpecs(newSpecsArr || []);
      setValueInput('');
    }
  };

  const onAddNewRow = () => {
    if (!valueInput?.trim()) {
      snackBar.show({
        severity: 'error',
        message: 'Input is required and must follow the format: title: value'
      });
      return;
    }

    const newSpecs: ProductCustomSpecifications = [];

    const lines = valueInput.split('\n').filter((line) => line.trim());

    for (const line of lines) {
      const colonIndex = line.indexOf(':');
      if (colonIndex === -1) {
        snackBar.show({
          severity: 'error',
          message: `Line missing colon separator: "${line}"`
        });
        return;
      }

      const title = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();

      if (!title || !value) {
        snackBar.show({
          severity: 'error',
          message: `Invalid format in line: "${line}"`
        });
        return;
      }

      newSpecs.push({ title, value });
    }

    let currentSpecs = cloneDeep(customSpecs) || [];
    currentSpecs = [...currentSpecs, ...newSpecs];

    onChangeCustomSpecsInternal(currentSpecs);
  };

  const onDeleteRow = (index: number) => {
    let currentSpecs = cloneDeep(customSpecs) || [];
    currentSpecs.splice(index, 1);
    onChangeCustomSpecsInternal(currentSpecs || []);
  };

  return (
    <div>
      <CardContent>
        <Typography className={classes.subHeader} variant="h6">
          Product Custom Specification
        </Typography>
        <Typography variant="caption" style={{ marginTop: 3 }}>
          Note: Use the format title: value on each line. To add more
          specifications, press Enter after each line.
        </Typography>
        <div className={classes.fieldsContainer}>
          <TextField
            required
            fullWidth
            name="value"
            placeholder={
              'title1: sample value or description for the specification\n\n' +
              'title2: sample value or description for the specification\n\n' +
              'title3: sample value or description for the specification'
            }
            variant="outlined"
            value={valueInput || ''}
            onChange={(e) => setValueInput(e.target.value)}
            multiline
            minRows={3}
          />
        </div>

        <div>
          <Button
            className={classes.addBtn}
            fullWidth
            onClick={onAddNewRow}
            color="primary"
            variant="contained"
          >
            Parse and Add
          </Button>
        </div>

        <div className={classes.customSpecsContainer}>
          {customSpecs.length > 0 && (
            <Typography
              variant="h6"
              style={{ marginTop: 10, marginBottom: 10 }}
            >
              Specifications:
            </Typography>
          )}
          {customSpecs?.map((x, i) => (
            <Box key={`${x}${i}`} display="flex" alignItems="flex-start" mb={1}>
              <IconButton
                className={classes.btnIcon}
                onClick={() => onDeleteRow(i)}
                size="small"
                style={{ marginTop: 4 }}
              >
                <DeleteIcon fontSize="small" color="secondary" />
              </IconButton>

              <Typography
                variant="body1"
                style={{
                  whiteSpace: 'pre-line',
                  display: 'inline-block'
                }}
              >
                <strong>{x?.title}:</strong> {x?.value}
              </Typography>
            </Box>
          ))}
        </div>
      </CardContent>
      {hasDivider ? <Divider /> : null}
    </div>
  );
};

export const AddProductInfoCustomSpecs = React.memo(component);
